import React, { useMemo } from "react";

const EdgeDurationHistogramSettings = ({
  graphData,
  objectType,
  onObjectTypeChange,
  sourceActivity,
  onSourceActivityChange,
  targetActivity,
  onTargetActivityChange,
  availableObjectTypes,
  loading,
  edgeSelectedFromGraph = false,
}) => {
  // Extract available activities from graph data based on selected object type
  const availableActivities = useMemo(() => {
    if (!graphData?.edges_performance?.event_couples || !objectType) {
      return [];
    }

    const objectEdges = graphData.edges_performance.event_couples[objectType];
    if (!objectEdges) {
      return [];
    }

    // Extract unique activities from edge keys
    const activitySet = new Set();
    Object.keys(objectEdges).forEach((edgeKey) => {
      const [from, to] = edgeKey.split(",");
      if (from) activitySet.add(from);
      if (to) activitySet.add(to);
    });

    return Array.from(activitySet).sort();
  }, [graphData, objectType]);

  // Get valid edges for the selected source activity
  const availableTargetsForSource = useMemo(() => {
    if (
      !graphData?.edges_performance?.event_couples ||
      !objectType ||
      !sourceActivity
    ) {
      return [];
    }

    const objectEdges = graphData.edges_performance.event_couples[objectType];
    if (!objectEdges) {
      return [];
    }

    const targets = new Set();
    Object.keys(objectEdges).forEach((edgeKey) => {
      const [from, to] = edgeKey.split(",");
      if (from === sourceActivity && to) {
        targets.add(to);
      }
    });

    return Array.from(targets).sort();
  }, [graphData, objectType, sourceActivity]);

  return (
    <div className="space-y-6">
      {/* Graph Selection Indicator */}
      {edgeSelectedFromGraph && sourceActivity && targetActivity && (
        <div className="p-3 bg-blue-50 border border-blue-300 rounded-md">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-900">
                Edge Selected from Process Graph
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {sourceActivity} → {targetActivity}
                {objectType && ` (${objectType})`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edge Selection Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
          Edge Selection
        </h3>

        {/* Object Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Object Type
          </label>
          <select
            value={objectType}
            onChange={(e) => {
              onObjectTypeChange(e.target.value);
              // Reset activities when object type changes
              onSourceActivityChange("");
              onTargetActivityChange("");
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading || availableObjectTypes.length === 0}
          >
            <option value="">Select object type...</option>
            {availableObjectTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {availableObjectTypes.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">
              No object types available
            </p>
          )}
        </div>

        {/* Source Activity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Source Activity
          </label>
          <select
            value={sourceActivity}
            onChange={(e) => {
              onSourceActivityChange(e.target.value);
              // Reset target when source changes
              onTargetActivityChange("");
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={
              loading || !objectType || availableActivities.length === 0
            }
          >
            <option value="">Select source activity...</option>
            {availableActivities.map((activity) => (
              <option key={activity} value={activity}>
                {activity}
              </option>
            ))}
          </select>
          {objectType && availableActivities.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">
              No activities found for selected object type
            </p>
          )}
        </div>

        {/* Target Activity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Activity
          </label>
          <select
            value={targetActivity}
            onChange={(e) => onTargetActivityChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={
              loading ||
              !sourceActivity ||
              availableTargetsForSource.length === 0
            }
          >
            <option value="">Select target activity...</option>
            {availableTargetsForSource.map((activity) => (
              <option key={activity} value={activity}>
                {activity}
              </option>
            ))}
          </select>
          {sourceActivity && availableTargetsForSource.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">
              No valid target activities for selected source
            </p>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-xs text-blue-800">
          <strong>💡 Tip:</strong> You can select an edge in two ways:
        </p>
        <ul className="text-xs text-blue-700 mt-2 ml-4 space-y-1 list-disc">
          <li>
            <strong>Click an edge</strong> in the Process Graph - then open
            Settings to see the pre-filled selection
          </li>
          <li>
            <strong>Use the dropdowns above</strong> to manually select Object
            Type, Source, and Target activities
          </li>
        </ul>
        <p className="text-xs text-blue-700 mt-2">
          The histogram shows the frequency distribution of durations between
          activities (X-axis: duration, Y-axis: object count).
        </p>
      </div>
    </div>
  );
};

export default EdgeDurationHistogramSettings;
